/**
 * Chantier : -
 * Regle metier : USR-R11 (PRODUIT/domaine/15_autorisations.md)
 * Decision : -
 * Hook de timeout d'inactivite UI (3h, warning a -1 min) : applique USR-R11
 * (auto-logout 3h d'inactivite) cote front. Coordination cross-onglets via
 * localStorage pour qu'une activite dans n'importe quel onglet reset la
 * fenetre. Throttle d'ecriture 5s pour limiter les writes de localStorage.
 */
import { useEffect, useLayoutEffect, useRef } from 'react';
import { toast } from 'sonner';

const IDLE_MS = 3 * 60 * 60_000;
const WARN_MS = 60_000;
const CHECK_INTERVAL_MS = 10_000;
const ACTIVITY_THROTTLE_MS = 5_000;
const STORAGE_KEY = 'serme:lastActivity';
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'] as const;

type Options = {
  disabled?: boolean;
};

export function useInactivityTimeout(onTimeout: () => void, options: Options = {}) {
  const onTimeoutRef = useRef(onTimeout);
  useLayoutEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);
  const { disabled = false } = options;

  useEffect(() => {
    if (disabled) return;

    let lastWrite = 0;
    let warningToastId: string | number | null = null;
    let intervalId: number | undefined;

    const readLastActivity = (): number => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? Number(raw) : NaN;
        return Number.isFinite(parsed) ? parsed : Date.now();
      } catch {
        return Date.now();
      }
    };

    const writeActivity = (ts: number) => {
      try {
        localStorage.setItem(STORAGE_KEY, String(ts));
      } catch {
        // localStorage indisponible (mode prive, quota) : on ne casse pas le hook
      }
    };

    const dismissWarning = () => {
      if (warningToastId !== null) {
        toast.dismiss(warningToastId);
        warningToastId = null;
      }
    };

    writeActivity(Date.now());

    const bumpActivity = () => {
      const now = Date.now();
      if (now - lastWrite < ACTIVITY_THROTTLE_MS) return;
      lastWrite = now;
      writeActivity(now);
      dismissWarning();
    };

    const checkIdle = () => {
      const elapsed = Date.now() - readLastActivity();
      if (elapsed >= IDLE_MS) {
        dismissWarning();
        if (intervalId !== undefined) {
          window.clearInterval(intervalId);
          intervalId = undefined;
        }
        onTimeoutRef.current();
        return;
      }
      if (elapsed >= IDLE_MS - WARN_MS && warningToastId === null) {
        warningToastId = toast.warning('Vous serez deconnecte dans 1 minute pour inactivite.', {
          duration: WARN_MS,
          action: {
            label: 'Rester connecte',
            onClick: () => {
              const now = Date.now();
              lastWrite = now;
              writeActivity(now);
              dismissWarning();
            },
          },
        });
      }
    };

    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) dismissWarning();
    };

    intervalId = window.setInterval(checkIdle, CHECK_INTERVAL_MS);
    ACTIVITY_EVENTS.forEach((evt) => {
      window.addEventListener(evt, bumpActivity, { passive: true });
    });
    window.addEventListener('storage', handleStorage);

    return () => {
      if (intervalId !== undefined) window.clearInterval(intervalId);
      ACTIVITY_EVENTS.forEach((evt) => {
        window.removeEventListener(evt, bumpActivity);
      });
      window.removeEventListener('storage', handleStorage);
      dismissWarning();
    };
  }, [disabled]);
}
